import type { TourStep } from "./types";

/**
 * The demo script, in the order of the RCM workflow spec's fourteen stages (S1–S14).
 *
 * Every `say` line was written against the screen's real component (title, panels, buttons),
 * not from memory — if a screen changes, its step here is what goes stale. Language is for a
 * partner or a practice owner, not a biller: say what a screen is *for* before what is on it.
 *
 * `connected` describes the planned integration: the practice's own booking, EHR or point-of-sale
 * system (any partner — the product names none), plus the RCM platform for clearinghouse
 * traffic. Today the demo runs on seeded data, so that box is the honest place to say "this is
 * where the live connection lands".
 *
 * Two chapters were added after the first client demos: **clinical documentation** (the EMR —
 * notes, plans of care, intake, charges from the note, the superbill), and the post-demo
 * billing work (denials from the payment file, appeal outcomes, automatic secondary claims,
 * the ICD-10-CM check). Every one was clicked through against the screen it names.
 */
export const TOUR_STEPS: readonly TourStep[] = [
  // ── Welcome ────────────────────────────────────────────────────────────────
  {
    id: "welcome",
    chapter: "Welcome",
    title: "Sarva — clinical notes and insurance billing, end to end",
    to: "/",
    say: [
      "This is Sarva: the clinical record (notes, plans of care, intake) and everything about getting paid, from the booking to a zero balance.",
      "A practice can use it as its EMR for cash patients and as its PMS for insured ones — or both at once.",
      "We will follow visits through the clinical record and then the 14 billing stages of our RCM workflow spec.",
      "Everything you see is real software running on demo data. No slides.",
      "The AI does the reading and proposing; a person always approves anything that moves money.",
    ],
    pointAt: "The left menu — it is ordered by how often each screen is used.",
  },
  {
    id: "dashboard",
    chapter: "Welcome",
    title: "The dashboard: where the money stands",
    to: "/",
    highlight: 'section[aria-label="Financial position"]',
    say: [
      "Top row: money owed to the practice (accounts receivable), how much is older than 90 days, the denial rate, the collection rate, and the cash collected this month.",
      "Receivable by age: the older a bill, the less likely it gets paid. Red bars are the risky ones.",
      "Exposure by escalation band: money at risk because a deadline is close or already missed.",
      "Waiting on a decision: the few things that need a person today.",
    ],
    pointAt:
      "The figure cards, then the two charts, then 'Waiting on a decision'.",
    connected:
      "These numbers would include every insured visit arriving from the practice's own booking or EHR system.",
  },

  // ── S1 Scheduling + pre-registration ────────────────────────────────────────
  {
    id: "s1-schedule",
    chapter: "S1 · Booking and registration",
    title: "Schedule: the visits coming in",
    to: "/schedule",
    say: [
      "Every booked visit, with the patient, provider and status.",
      "The Billing column tells the front desk if insurance is involved.",
      "'Coverage checks due' lists visits whose insurance has not been checked yet.",
      "Clicking a visit opens it: check in, complete, cancel, or link a prior authorization.",
    ],
    pointAt: "The Billing column, then the 'Coverage checks due' panel.",
    connected:
      "If the practice books in another system, each booking reaches Sarva automatically and creates the visit here — the front desk does not type anything twice.",
  },
  {
    id: "s1-setup",
    chapter: "S1 · Booking and registration",
    title: "Scheduling setup: visit types and working hours",
    to: "/scheduling-setup",
    say: [
      "Visit types (new patient 40 minutes, follow-up 20 minutes, procedure…), each provider's working hours, and time off.",
      "This is set once when a practice is onboarded.",
    ],
    connected:
      "A practice that schedules in another system keeps scheduling there. Sarva only needs to know which services can be billed to insurance — that is the Service map, two steps ahead.",
  },
  {
    id: "s1-patients",
    chapter: "S1 · Booking and registration",
    title: "Patients",
    to: "/patients",
    say: [
      "Every patient, searchable by name, date of birth or record number.",
      "The Coverage column shows the patient's insurance — click it to see their eligibility.",
      "The patient's name opens their full chart.",
    ],
    pointAt: "A patient name and the Coverage link (both shown as links).",
    connected:
      "Patients are created from the partner system's record — demographics come across, the front desk adds only the insurance.",
  },
  {
    id: "s1-imports",
    chapter: "S1 · Booking and registration",
    title: "Data import: bringing a practice over",
    to: "/imports",
    say: [
      "When a practice moves to Sarva, its open bills come over from the old system here.",
      "AI proposes which column in their file means what — nothing is loaded until a person agrees.",
      "Anything that does not fit is listed as an exception for someone to fix, not silently dropped.",
    ],
  },
  {
    id: "s1-service-map",
    chapter: "S1 · Booking and registration",
    title: "Service map: what each clinic service bills as",
    to: "/service-map",
    say: [
      "A clinic calls a service 'Dermatology follow-up, 15 min'. Insurance needs a billing code: 99213.",
      "This screen holds that translation for every service the clinic sells.",
      "It also marks what is never billed to insurance — Botox for cosmetic reasons, retail products, memberships.",
      "Unmapped services are the to-do list: until someone maps them, they cannot be billed.",
    ],
    pointAt:
      "First switch the practice (top bar) to Riverbend Dermatology & Aesthetics — the clinic whose services come from a partner system. Then the Partner service and Code columns, and a row marked as not billable to insurance.",
    connected:
      "This is the key to connecting any partner system: each of its services maps here once, then every invoice line knows whether it goes to insurance or stays with the patient.",
  },

  // ── S2 / S4 Insurance capture + eligibility ─────────────────────────────────
  {
    id: "s4-chart",
    chapter: "S2 + S4 · Insurance and eligibility",
    title: "The patient chart: insurance on file",
    to: "/patients",
    match: /^\/patients\/[^/]+$/,
    doThis: "Click any patient's name in the list.",
    say: [
      "One page per patient: what they owe, what insurance still owes, and whether we may ask them for money yet.",
      "Coverage tab: their insurance policies, primary and secondary.",
      "Insurance card: AI reads the card and suggests payer, member ID and group — a person reviews before anything is saved.",
      "Other tabs: the account ledger, prior authorizations and referrals, consents, and notes.",
    ],
    pointAt:
      "The three figures at the top, then the Coverage tab and the 'Insurance card' panel.",
    connected:
      "At check-in the front desk clicks 'Bill to insurance' in the partner system. Sarva opens already signed in, with this patient and the visit filled in — they take a photo of the card and confirm.",
  },
  {
    id: "s2-eligibility",
    chapter: "S2 + S4 · Insurance and eligibility",
    title: "Eligibility: is the patient covered, and what will they pay?",
    match: /^\/patients\/[^/]+\/eligibility$/,
    doThis:
      "On the patient chart, open Actions → Check eligibility (or click Coverage on the Patients list).",
    say: [
      "Before the visit, Sarva asks the insurer: is this person covered today?",
      "The answer shows the plan, coverage dates, and if refused, why.",
      "'Read benefits': AI turns the insurer's coded reply into plain copay, deductible and coinsurance, each with a confidence level.",
      "Estimate: pick the procedure and date, and see what the patient will likely owe — so the clinic can collect it at checkout.",
    ],
    pointAt: "The check result panel, then the Estimate panel.",
    connected:
      "The check goes to the insurer electronically through Sarva's RCM platform (the 270/271 transaction), automatically 3 days before the visit and again at check-in.",
    ai: {
      doThis: "Click 'Check eligibility' (the answer is back in seconds), then 'Read benefits'.",
      expect:
        "In about 10 seconds: copay, deductible and out-of-pocket maximum, each with its own confidence score, marked 'Proposed'. Say: nobody retypes the insurer's codes, and nothing is saved until a person accepts it.",
    },
  },

  // ── S3 Prior authorization ──────────────────────────────────────────────────
  {
    id: "s3-prior-auth",
    chapter: "S3 · Prior authorization",
    title: "Prior authorization: permission before treatment",
    to: "/prior-auth",
    say: [
      "Some treatments need the insurer's permission first — for example phototherapy or Mohs surgery.",
      "Each authorization has a number, the units allowed, the dates, and how many are used.",
      "'Expiring' shows the ones about to run out, so a visit is never done on an expired one.",
      "Payer rules: which insurer needs authorization for which procedure.",
    ],
    pointAt: "The Expiring tab, then the Used / Remaining columns.",
    connected:
      "A claim that needs an authorization is blocked from going out until one is on file — no wasted denials.",
  },
  {
    id: "s3-referrals",
    chapter: "S3 · Prior authorization",
    title: "Referrals",
    to: "/referrals",
    say: [
      "Some plans need a referral from the patient's primary doctor.",
      "This lists referrals expiring soon across every patient, so the clinic can ask for a new one in time.",
    ],
  },

  // ── Clinical documentation (EMR) ────────────────────────────────────────────
  //
  // ADR 0017. The physical-therapy practice (RVB-PT) is seeded by `demo_clinical`: its
  // templates, timed codes and billing rules come from the physical-therapy specialty pack.
  {
    id: "emr-setup",
    chapter: "Clinical documentation (EMR)",
    title: "Clinical setup: a specialty is data, not software",
    to: "/clinical-setup",
    say: [
      "Sarva has no 'physical therapy module'. A specialty is a pack: note templates, which codes are billed in 15-minute units, and the billing rules.",
      "The physical-therapy pack is installed here. The same screen would install chiropractic or occupational therapy tomorrow, with no new release.",
      "The pack asks for the things it cannot know — which of the practice's payers follow Medicare's therapy rules, and this year's KX threshold.",
      "Everything it installs is then the practice's to change, and reinstalling never overwrites what the practice changed.",
    ],
    pointAt:
      "The 'Specialty packs' tab (Preview shows exactly what an install would do), then 'Charge rules' — GP, 59, KX, progress report due.",
  },
  {
    id: "emr-chart",
    chapter: "Clinical documentation (EMR)",
    title: "The patient's clinical record",
    to: "/patients",
    match: /^\/patients\/[^/]+$/,
    doThis:
      "Switch the practice (top bar) to Riverbend Physical Therapy, then click Jordan Avery and open the Clinical tab.",
    say: [
      "The plan of care: how many visits the insurer authorised and how many are used — the front desk schedules against it.",
      "Every note — evaluation, daily notes, progress report — signed by the clinician who wrote it.",
      "Outcomes over time: the patient's own functional score from each signed note (here the Patient-Specific Functional Scale, 3.0 to 6.3). Insurers ask for exactly this to keep paying.",
    ],
    pointAt: "'Plans of care', then 'Clinical notes', then 'Outcomes over time'.",
  },
  {
    id: "emr-note",
    chapter: "Clinical documentation (EMR)",
    title: "Writing today's note — the AI drafts, the clinician signs",
    to: "/patients",
    match: /^\/clinical-notes\/[^/]+$/,
    doThis: "In 'Clinical notes', open today's daily note (it says draft).",
    say: [
      "The note is the practice's own template. Every change saves itself; a required field still empty is named before signing.",
      "Billable time is entered as minutes. The units come from the insurer's rule — Medicare's 8-minute rule here — calculated by Sarva, never typed.",
      "Signing makes the note permanent. A correction afterwards is an addendum, so the record always shows what was signed.",
    ],
    pointAt: "'AI assistance', then 'Billable time', then 'Sign note'.",
    ai: {
      doThis:
        "Paste the dictation below into 'Dictation or bullet notes' and click 'Draft the note'. When it appears, click 'Apply to note'. Then in 'Billable time' add 97110 for 23 minutes and 97140 for 15, click 'Save time', and finally 'Sign note'.",
      paste:
        "Jordan reports sitting through a two-hour meeting with mild stiffness only. Pain 3 out of 10 today. Performed core stabilisation, dead bugs and bird dogs, 3 sets of 10, and lumbar mobilisation grade III. Tolerated well with no increase in pain. Progressing toward the return-to-tennis goal. Next visit add rotational loading. Home program updated.",
      expect:
        "About 10 seconds: the patient report, pain 3, response, assessment, plan and 'home program updated' are filled, each showing the dictation's words it came from; fields the dictation did not mention stay empty. Units appear after 'Save time' (38 minutes: 2 + 1 under the 8-minute rule). Say: the AI only fills what the clinician said, it never fills billable minutes or a questionnaire score, and only the clinician can sign.",
    },
  },
  {
    id: "emr-charges",
    chapter: "Clinical documentation (EMR)",
    title: "From the signed note to the charges",
    to: "/patients",
    match: /^\/clinical-notes\/[^/]+$/,
    doThis:
      "Back on Jordan Avery's Clinical tab, open the signed daily note from about two weeks ago — its charges are not committed yet.",
    say: [
      "Signed time becomes charges with the insurer's rules applied, and each modifier says which rule added it.",
      "GP is required on therapy billed to insurance, so it cannot be unticked. 59 is only proposed on manual therapy billed with therapeutic activities — the clinician confirms the services were separate.",
      "'Commit charges' sends them into the billing workflow: the same claim, scrubbing and follow-up as every other visit.",
    ],
    pointAt: "The modifiers on each line (required vs proposed), then 'Commit charges'.",
    ai: {
      doThis: "Before committing, click 'Check documentation' in 'AI assistance'.",
      expect:
        "About 10 seconds: either 'No gaps found' or findings such as a billed service the note does not describe, minutes the note does not account for, or missing medical necessity — each naming the line. Say: it is advice for the clinician or the biller; it never blocks signing or billing, and a person acknowledges or rejects every finding.",
    },
  },
  {
    id: "emr-superbill",
    chapter: "Clinical documentation (EMR)",
    title: "Cash patients: the superbill",
    to: "/patients",
    match: /^\/clinical-notes\/[^/]+$/,
    doThis:
      "Open Sam Delgado (a cash patient) → Clinical tab → the signed evaluation, then click 'Superbill'.",
    say: [
      "A cash patient pays at the desk and claims from their own insurer. The superbill is what they send.",
      "It carries the practice's tax ID and NPIs, the diagnoses, every code with its units and the practice's own description, and what was paid.",
      "Money taken at the desk is shown as received that day, so the receipt is right even before it is applied to anything.",
    ],
    pointAt: "The 'Superbill' button in the Charges panel, then the downloaded PDF.",
  },
  {
    id: "emr-intake",
    chapter: "Clinical documentation (EMR)",
    title: "Intake: the patient fills it in before the visit",
    to: "/patients",
    match: /^\/patients\/[^/]+$/,
    doThis: "Open Riley Chen → Clinical tab.",
    say: [
      "'Patient intake' creates a single-use link — sent by text or email, or opened on a tablet at the front desk. No account, no password.",
      "Riley has already completed it: the answers arrive as an intake note marked 'Completed by the patient'.",
      "The clinician reviews and signs it. Nobody edits what the patient wrote — corrections are an addendum.",
      "The plan of care is in draft until the evaluation; activating it starts the insurer's certification clock where one applies.",
    ],
    pointAt: "The intake note in 'Clinical notes', then the 'Patient intake' panel.",
  },

  // ── S5 Charge capture ──────────────────────────────────────────────────────
  {
    id: "s5-charges",
    chapter: "S5 · Charge capture (superbill)",
    title: "Charge capture: what was done in the visit",
    to: "/charges",
    say: [
      "After the visit, each service becomes a charge: the procedure code, units, price and the diagnosis it treats.",
      "'Visits to code' is the worklist — visits that have nothing billed yet.",
      "Clicking a visit shows its charge lines; a line can be held if something needs checking.",
      "Prices come from the practice's fee schedule automatically.",
      "Every diagnosis code is checked against the official ICD-10-CM list for the visit's date — a code that did not exist that year is caught here, not by the insurer.",
    ],
    pointAt:
      "The 'Visits to code' panel, then open one visit to show its lines.",
    connected:
      "When the visit comes from a partner system, each of its lines is already here; the provider marks it medical or not, and Sarva suggests the codes from the Service map. When the visit was documented in Sarva, the charges come from the signed note (the Clinical documentation chapter).",
    // D153/D154. The real model read this sample (PNG and PDF) correctly in 6–8 s against a
    // local API on 2026-10-14; not yet clicked through on the hosted demo.
    ai: {
      doThis:
        "Download the sample paper superbill (/demo/sample-superbill.png — or the .pdf) to the laptop. Open any visit from 'Visits to code', scroll to 'Upload superbill', choose the file and click 'Read superbill'. When the lines appear, accept the five lines, then 'Create charges'.",
      expect:
        "In about 10 seconds the form's ticked boxes come back as five lines — 99213 with modifier 25, 36415, 81002, 90686 and 90471 — each with the diagnoses it points at (I10, E11.9, Z23) and the label printed beside it. Say: AI only reads what the provider ticked; the prices on the paper are ignored, the practice's fee schedule prices every charge, and nothing is billed until a person accepts each line. If the date printed on the form isn't this visit's date, a warning says so — that's the guard against uploading a superbill to the wrong visit. The diagnoses are checked against the official ICD-10-CM list for the visit date; the procedure codes show 'Not checked' because CPT is licensed by the AMA and not loaded in the demo.",
    },
  },

  // ── S6 Coding + scrubbing ──────────────────────────────────────────────────
  {
    id: "s6-coding",
    chapter: "S6 · Coding and checks",
    title: "Coding: does the note support the bill?",
    to: "/coding",
    say: [
      "Coded visits, most recent first.",
      "'Review note': paste the provider's note and AI reads what it documents.",
      "AI then suggests the right visit level — and flags if the note supports more than was billed (money left on the table) or less (audit risk).",
      "A certified coder accepts, rejects or changes every suggestion. Nothing changes on its own.",
    ],
    pointAt: "The 'Review note' button on a row.",
    ai: {
      doThis:
        "Click 'Review note' on any row, paste the note below, click 'Open review'. Then 'Read note', and once it fills in, 'Suggest code'.",
      paste:
        "Established patient, follow-up for hypertension and type 2 diabetes. Reports occasional morning headaches. BP 148/92, A1c 7.9 today, up from 7.1. Reviewed home glucose log and labs. Increased lisinopril to 20 mg daily, added metformin XR 500 mg. Discussed diet and side effects. Two chronic conditions, one worsening; prescription drug management. Return in 4 weeks.",
      expect:
        "'Read note' (~20 s) lists what the note documents: complaint, problems, data reviewed, risk, plan. 'Suggest code' (~20 s) finds the note supports level 4 while 99213 (level 3) was billed — and refuses to change the bill by itself: it raises a work item for a coder. Say: the AI found money left on the table; a certified coder decides.",
    },
  },
  {
    id: "s6-scrub",
    chapter: "S6 · Coding and checks",
    title: "Payer edits: catching mistakes before the insurer does",
    to: "/scrub-rules",
    say: [
      "Every insurer has its own rules about what can be billed together.",
      "These checks run on every claim before it is sent, so errors are fixed here instead of coming back as denials weeks later.",
      "A new rule starts as a draft, is measured against past claims, and only then is put in force by an approver.",
    ],
    pointAt: "The Status and 'False positives' columns.",
  },

  // ── S7 Claim submission ────────────────────────────────────────────────────
  {
    id: "s7-claims",
    chapter: "S7 · Sending the claim",
    title: "Claims: every bill sent to insurance",
    to: "/claims",
    say: [
      "Every claim with its status, what was charged, what was paid, and what is still owed.",
      "Filter by status or by insurer; export to a spreadsheet with one click.",
      "Click any claim to see everything about it.",
    ],
    pointAt: "The Status, Paid and Balance columns.",
  },
  {
    id: "s7-claim-detail",
    chapter: "S7 · Sending the claim",
    title: "One claim, line by line",
    to: "/claims",
    match: /^\/claims\/[^/]+$/,
    doThis: "Click any claim in the list.",
    say: [
      "Top: total charged, paid, adjustments, what the patient owes, and the balance.",
      "Service lines: every procedure on the claim, and what the insurer allowed and paid for each one.",
      "Overview: the pre-submission check and every filing attempt.",
      "Actions: file the claim, check its status, download the paper form (CMS-1500), correct or void it.",
    ],
    pointAt: "The figures at the top, then the 'Service lines' tab.",
    connected:
      "'File claim' sends it electronically through the RCM platform (the 837P). Each line carries its own ID, so the payment can be matched back to the exact line it paid.",
  },
  {
    id: "s7-submissions",
    chapter: "S7 · Sending the claim",
    title: "Claim submissions: did it actually go out?",
    to: "/claim-submissions",
    say: [
      "Every attempt to send a claim, newest first.",
      "If the outcome of a send is unclear, Sarva never sends it again blindly — that would create a duplicate. It asks a person.",
    ],
  },
  {
    id: "s7-acks",
    chapter: "S7 · Sending the claim",
    title: "Acknowledgements: did the insurer accept it?",
    to: "/acknowledgements",
    say: [
      "Within hours of sending, the clearinghouse and insurer reply: accepted, or rejected with a reason.",
      "A rejected claim was never really filed — and the filing deadline keeps running. So rejections land here for someone to fix and refile.",
    ],
    pointAt: "The Result and Reason columns.",
    connected:
      "These replies (999 and 277CA) arrive automatically from the RCM platform.",
  },

  // ── S8 Claim status follow-up ──────────────────────────────────────────────
  {
    id: "s8-queue",
    chapter: "S8 · Follow-up",
    title: "Work queue: what a biller does today",
    to: "/queue",
    say: [
      "One list of everything that needs a person, soonest deadline first, then the biggest money.",
      "Each item says what is at stake and links to the claim or patient.",
      "Items can be assigned to a team member.",
    ],
    pointAt: "The Deadline and 'At stake' columns.",
    connected:
      "Claim status checks run on a schedule through the RCM platform (276/277). A claim that stops moving for 30 days creates an item here automatically.",
  },
  {
    id: "s8-obligations",
    chapter: "S8 · Follow-up",
    title: "Obligations: every deadline that costs money if missed",
    to: "/obligations",
    say: [
      "Insurers give a fixed time to file or appeal. Miss it and the money is gone for good.",
      "Sarva tracks every one of those deadlines, most urgent first, with the amount at risk.",
      "'If missed' says exactly what is lost.",
    ],
    pointAt: "The Due, 'At risk' and 'If missed' columns.",
  },
  {
    id: "s8-follow-ups",
    chapter: "S8 · Follow-up",
    title: "Follow-ups due",
    to: "/follow-ups",
    say: [
      "When an insurer says 'we will process it in 2 weeks', the biller records that promise.",
      "This lists promises that are due or overdue, oldest first, so nothing is forgotten.",
    ],
  },

  // ── S9 Remittance ──────────────────────────────────────────────────────────
  {
    id: "s9-remittance",
    chapter: "S9 · Payment arrives",
    title: "Remittance: what the insurer paid, and why",
    to: "/remittance",
    say: [
      "When an insurer pays, it sends a payment file explaining every line: paid, adjusted, or the patient's share.",
      "Paper EOB? AI reads the scan as a proposal — nothing is posted from it without review.",
      "Each payment shows whether it is matched to claims and reconciled to the bank.",
      "A line the insurer refused becomes a denial automatically, with its reason code (CARC) translated into plain words — nobody re-reads the payment file to find them.",
    ],
    pointAt:
      "The 'Add remittance' menu, then the Matching and Reconciliation columns.",
    connected:
      "Payment files (835) arrive automatically through the RCM platform and are matched line by line using the IDs we sent on the claim.",
  },

  // ── S10 Payment posting ────────────────────────────────────────────────────
  {
    id: "s10-posting",
    chapter: "S10 · Posting the payment",
    title: "Posting: one click, but only when it balances",
    to: "/posting",
    say: [
      "Pick a deposit and Sarva has already matched it to the claims.",
      "'Post deposit' stays disabled until every dollar is accounted for — the system will not post money that does not balance.",
      "A mistaken posting is never edited or deleted: it is reversed with a new entry, so the history stays true.",
    ],
    pointAt:
      "The deposits list, then the disabled/enabled 'Post deposit' button.",
    connected:
      "After posting, the insurance amount can be written back to the partner system's invoice as an 'Insurance' payment, so the clinic sees it there too.",
  },
  {
    id: "s10-bank",
    chapter: "S10 · Posting the payment",
    title: "Bank reconciliation: did the money really arrive?",
    to: "/bank-reconciliation",
    say: [
      "Load the bank statement and Sarva matches each insurance payment to a real bank deposit.",
      "Anything paid on paper but not in the bank shows up here.",
    ],
  },

  // ── S11 Denial management ──────────────────────────────────────────────────
  {
    id: "s11-denials",
    chapter: "S11 · Denials and appeals",
    title: "Denials: money refused, largest first",
    to: "/denials",
    say: [
      "When an insurer refuses to pay a line, it lands here, biggest amount first.",
      "Each denial gets a root cause: eligibility, authorization, coding, bundling, timely filing…",
      "Knowing the cause tells the team how to fix it — and how to stop it happening again.",
      "Denials arrive from the insurer's payment file itself, with the reason code shown on every row.",
    ],
    pointAt: "The 'Denied amount', 'Root cause' and Recovered columns.",
  },
  {
    id: "s11-denial-detail",
    chapter: "S11 · Denials and appeals",
    title: "One denial: AI suggests why",
    to: "/denials",
    match: /^\/denials\/[^/]+$/,
    doThis: "Click any denial in the list.",
    say: [
      "'Ask for a suggestion': AI reads only the denial codes — no patient information — and suggests the root cause.",
      "It shows how confident it is and why it thinks so.",
      "The person accepts, corrects or rejects it. Every decision is recorded.",
    ],
    pointAt: "The 'AI suggestion' panel.",
    ai: {
      doThis: "Click 'Ask for a suggestion'.",
      expect:
        "About 15 seconds: a proposed root cause with a confidence score, what on the claim supports it, and the model's reasoning in plain words. Say: only the denial codes left the system — no patient data — and the person accepts, corrects or rejects it.",
    },
  },
  {
    id: "s11-appeals",
    chapter: "S11 · Denials and appeals",
    title: "Appeals: fighting a wrong denial",
    to: "/appeals",
    say: [
      "A denial is the start of the work, not the end.",
      "Each appeal shows the amount, the deadline to appeal, and its status.",
      "Click an appeal: AI drafts the appeal letter, a person approves it, and only then can it be sent.",
      "When the insurer answers, 'Record outcome' marks it won, partly won or lost — so the practice sees which appeals pay off.",
    ],
    pointAt: "The 'Appeal window' column.",
    ai: {
      doThis: "Click an appeal whose status is 'Drafting', then 'Draft letter'.",
      expect:
        "About 20 seconds: a complete appeal letter citing the claim and the denial codes, marked 'Every citation grounded'. Say: it cannot go out until a person clicks Approve, and only then Send appeal.",
    },
  },

  // ── S12 Patient responsibility ─────────────────────────────────────────────
  {
    id: "s12-balance",
    chapter: "S12 · What the patient owes",
    title: "Patient balance",
    to: "/patient-balance",
    say: [
      "Once insurance has paid, what is left is the patient's share — taken from the insurer's answer, not a guess.",
      "Find a patient to see their estimates, statements sent, and who is responsible for the bill.",
      "'Balance explanation': AI writes a plain-language explanation of the bill for the patient.",
    ],
    pointAt: "'Find a patient', then 'Balance explanation'.",
    connected:
      "A card saved on file is charged according to the clinic's rules (for example, automatically under $50, with notice above). A charge is never retried without first checking the first one did not go through.",
  },
  {
    id: "s12-refunds",
    chapter: "S12 · What the patient owes",
    title: "Refunds and write-offs: always two people",
    to: "/refunds",
    say: [
      "If a patient paid too much, a refund is requested here.",
      "One person requests, a different person approves — no one can send money out alone.",
      "Write-offs work the same way.",
    ],
    pointAt: "The State and Action columns.",
  },

  // ── S13 Secondary insurance ────────────────────────────────────────────────
  {
    id: "s13-secondary",
    chapter: "S13 · Secondary insurance",
    title: "Secondary insurance",
    to: "/claims",
    say: [
      "Some patients have two insurances. After the first pays, the rest is billed to the second.",
      "Sarva holds both policies on the patient and the coordination of benefits on each claim (Claim facts tab).",
      "When the first insurer's payment is posted, Sarva produces the secondary claim automatically, with what the first insurer paid on it.",
      "The patient is billed once — only after the second insurer has answered — for what is truly left.",
    ],
  },

  // ── S14 Reconciliation + reporting ─────────────────────────────────────────
  {
    id: "s14-month-end",
    chapter: "S14 · Month-end and reports",
    title: "Month-end close",
    to: "/month-end",
    say: [
      "At month end, debits and credits must match to the cent.",
      "Only when the difference is zero can the owner close the month. After that, the month cannot be changed.",
      "The journal exports to the accountant.",
    ],
    pointAt: "The Difference figure and the 'Close' button.",
    connected:
      "Every night Sarva compares its numbers with the partner system's collections report and flags any difference.",
  },
  {
    id: "s14-reports",
    chapter: "S14 · Month-end and reports",
    title: "Reports",
    to: "/reports",
    say: [
      "AR aging, balance checks against the ledger, and exports.",
      "Below: losses already taken, money at risk, unclassified denials, and deposits that will not post.",
    ],
  },
  {
    id: "s14-scoreboard",
    chapter: "S14 · Month-end and reports",
    title: "Scoreboard: how well is billing working?",
    to: "/scoreboard",
    say: [
      "The month's key measures: how many claims were paid first time, money collected, and what needs input.",
      "This is the page a practice owner checks every week.",
    ],
  },
  {
    id: "s14-recovery",
    chapter: "S14 · Month-end and reports",
    title: "Recovery: money the practice is owed but not chasing",
    to: "/recovery",
    say: [
      "Sarva compares what insurers paid with what the contract says they should pay.",
      "Underpayments are found and turned into challenges or appeals with one sweep.",
    ],
    pointAt: "'Estimated underpayment' and the 'Run sweep' menu.",
  },

  // ── AI and control ─────────────────────────────────────────────────────────
  {
    id: "ai-agents",
    chapter: "AI, safety and control",
    title: "Agent activity",
    to: "/agents",
    say: [
      "The health of every work queue the automation feeds: how many items are open and how old the oldest is.",
    ],
  },
  {
    id: "ai-policy-bands",
    chapter: "AI, safety and control",
    title: "Policy bands: what the system may do on its own",
    to: "/policy-bands",
    say: [
      "For every kind of decision, the practice chooses: Observe (AI only watches), Assist (AI suggests, a person decides), or Auto.",
      "'Stop this now' switches any automation off immediately.",
      "Moving money is never automatic.",
    ],
  },
  {
    id: "ai-payer-rules",
    chapter: "AI, safety and control",
    title: "Payer rules: AI reads the insurer's policy",
    to: "/payer-rules",
    say: [
      "Paste an insurer's policy text and AI proposes the filing deadlines it contains, with the citation.",
      "A proposal does nothing until a person approves it.",
    ],
    pointAt: "The 'Awaiting review' tab, then the Citation column.",
    ai: {
      doThis:
        "Click 'Propose from policy text'. Payer: Gulf Coast Health Plan. Document: 'Provider manual 2026, §4 Claims'. Paste the text below, then 'Propose rules'.",
      paste:
        "4.1 Claims must be submitted within 120 days of the date of service. 4.2 A corrected claim must be submitted within 120 days of the date of the original remittance. 4.3 Requests for reconsideration must be received within 90 days of the initial determination.",
      expect:
        "About 20 seconds: three rules — initial filing, corrected claim, appeal — each quoting the sentence it came from, all 'Awaiting review'. Say: none of them governs a deadline until a person approves it.",
    },
  },
  {
    id: "ai-documents",
    chapter: "AI, safety and control",
    title: "Documents",
    to: "/documents",
    say: [
      "Scanned letters, EOBs and records: AI classifies each one and shows its reasoning.",
      "A person files it, files it as a different type, or rejects the suggestion.",
    ],
    pointAt: "The 'Classifications' panel, then the AI reasoning under each row.",
    // Written against the real dialog (Classify document → "Document text" → Classify) and
    // `review_classification`'s outcomes. Unlike the moments above, NOT yet clicked through on
    // the hosted demo — the integrator does that against the real model. The letter is fully
    // synthetic: a fictional payer, member and claim number, so it matches nothing in the
    // book and lands "Not matched" rather than filed against a real row.
    ai: {
      doThis:
        "Click 'Classify document', paste the letter below into 'Document text', then 'Classify'.",
      paste:
        "Gulf Coast Health Plan — Appeals Unit, PO Box 4410, Tampa FL. October 2, 2026. RE: Level 1 appeal decision. Member ID GCH-55-0192-77. Claim number GC-2026-0881345. Date of service: July 14, 2026. We reviewed your appeal of the denial of CPT 99214 (reason code CO-50, not medically necessary). After review of the submitted office note, the original decision is overturned. The claim will be reprocessed and payment of $142.60 issued within 30 days. If you have questions, call Provider Services at 1-800-555-0148.",
      expect:
        "About 20 seconds: a new row reading 'Appeal determination' with its confidence, the claim and member numbers it quoted, and the AI's reasoning. It says 'Not matched' — that claim number is not one of ours, so nothing is filed against a patient until a person says so. Say: AI reads the post; a person decides where it goes.",
    },
  },
  {
    id: "ai-audit",
    chapter: "AI, safety and control",
    title: "Audit log: every change, who, and why",
    to: "/audit",
    say: [
      "Every change in the system is recorded: who made it, what changed, and the reason.",
      "Including any access by Sarva's own staff to the practice's data.",
      "This is what HIPAA and insurer audits ask for.",
    ],
  },

  // ── How it connects ────────────────────────────────────────────────────────
  {
    id: "connects",
    chapter: "How it connects",
    title: "Working alongside the systems a practice already has",
    to: "/service-map",
    say: [
      "A practice keeps the systems it likes — booking, an EHR, a point of sale. Sarva does not ask it to switch.",
      "Sarva does the clinical record where it is wanted, and everything insurance: coverage, codes, claims, payments, denials, the patient's share.",
      "From the partner system, one 'Bill to insurance' click opens Sarva already signed in, with the visit filled in.",
      "Each partner service is mapped once here; after that every visit knows what goes to insurance and what stays with the patient.",
      "The clearinghouse side — eligibility, claims, status, payments — runs on Sarva's RCM platform, which already automates claim follow-up for our clients today.",
    ],
    pointAt: "This Service map — the bridge between a partner's service and an insurance code.",
  },
  {
    id: "close",
    chapter: "How it connects",
    title: "Thank you",
    to: "/",
    say: [
      "From the patient's intake to a signed note, a claim and a zero balance — one system, with AI reading and proposing, and people deciding.",
      "Nothing in it is built for one specialty: a new specialty is a pack of templates and rules, not new software.",
      "Questions?",
    ],
  },
];
